import { useCallback, useEffect, useState } from 'react';

import {
  clampPaneWidth,
  readStoredPaneWidth,
  writeStoredPaneWidth,
  type PaneWidthBounds,
} from '../lib/pane-width';

export interface PaneWidth {
  /** `undefined` while the stylesheet's own default is in force. */
  width: number | undefined;
  setWidth: (width: number) => void;
  resetWidth: () => void;
}

/**
 * One resizable pane's width, persisted.
 *
 * Persisted rather than held for the session because a pane width is a reading
 * preference, not a position in the queue: a reviewer who widened the rail to
 * read validator evidence wants it wide again tomorrow. The URL carries the
 * queue position; this is the one piece of the shell that is about the person
 * rather than the work.
 *
 * The preference and the width actually rendered are kept apart. The shell is
 * two fixed grid columns around one flexible reader, so a pane too wide for the
 * window overflows the page sideways rather than squeezing anything — but
 * clamping the stored value would mean a window narrowed for a minute
 * permanently loses the wide pane it was set to. So the preference is stored
 * whole and clamped only on the way out, against the window as it is now.
 *
 * `bounds` is expected to be a module constant: it is a dependency of the
 * returned setter, so an object literal would rebuild it on every render.
 */
export function usePaneWidth(
  paneKey: string,
  bounds: PaneWidthBounds
): PaneWidth {
  const [preferredWidth, setPreferredWidth] = useState<number | undefined>(() =>
    readStoredPaneWidth(paneKey)
  );
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleViewportResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, []);

  const setWidth = useCallback(
    (nextWidth: number) => {
      // Clamped on the way in as well, so what is stored is a width the
      // reviewer could actually see when they chose it — a drag that ran past
      // the edge should not persist a number the pane never had.
      const clampedWidth = clampPaneWidth(nextWidth, bounds, window.innerWidth);
      setPreferredWidth(clampedWidth);
      writeStoredPaneWidth(paneKey, clampedWidth);
    },
    [bounds, paneKey]
  );

  const resetWidth = useCallback(() => {
    setPreferredWidth(undefined);
    writeStoredPaneWidth(paneKey, undefined);
  }, [paneKey]);

  return {
    width:
      preferredWidth === undefined
        ? undefined
        : clampPaneWidth(preferredWidth, bounds, viewportWidth),
    setWidth,
    resetWidth,
  };
}
